import React, { FC } from 'react';
import styled from 'styled-components';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import {
  getDeliveryStatusLabel,
  getOrderDeliveryState,
  isCarrierDeliveredWithoutDate,
} from '../../../common/utils/services/order-delivery-utils';
import StringUtils from '../../../common/utils/services/string-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvOrderDeliveryDates from '../molecules/OvOrderDeliveryDates';
import OvRecordDetailsSection from '../molecules/OvRecordDetailsSection';

const OvOrderDeliverySection: FC<{ order: Order }> = ({ order }) => {
  const { t } = useTranslation();
  const deliveryState = getOrderDeliveryState(order);

  const context = [
    {
      label: t('orders.delivery.partner'),
      value: StringUtils.displayValue(order.dropshipping_partner),
    },
    {
      label: t('orders.delivery.submitted'),
      value: StringUtils.getPrettifiedBooleanValue(order.is_submitted),
    },
    {
      label: t('orders.delivery.method'),
      value: StringUtils.displayValue(order.delivery_method),
    },
    {
      label: t('orders.tableHeaders.fulfillmentStatus'),
      value: StringUtils.displayValue(order.fulfillment_status),
    },
  ];

  const renderDelivery = () => {
    switch (deliveryState.kind) {
      case 'unavailable':
        return (
          <QuietEmpty>
            <LocalShippingIcon aria-hidden="true" />
            <div>
              <strong>{t('orders.delivery.unavailable')}</strong>
              <p>
                {t('orders.delivery.unavailableDescription', {
                  partner: StringUtils.displayValue(order.dropshipping_partner),
                })}
              </p>
            </div>
          </QuietEmpty>
        );

      case 'noData':
        return (
          <QuietEmpty>
            <LocalShippingIcon aria-hidden="true" />
            <div>
              <strong>{t('orders.delivery.noData')}</strong>
              <p>{t('orders.delivery.noDataDescription')}</p>
            </div>
          </QuietEmpty>
        );

      case 'delivered':
      case 'overdue':
      case 'notConfirmed':
      case 'inTransit':
        return (
          <>
            {isCarrierDeliveredWithoutDate(order) && (
              <Discrepancy>
                <WarningIcon aria-hidden="true" />
                <strong>{t('orders.delivery.notConfirmed')}</strong>
                <span>
                  {t('orders.delivery.notConfirmedDescription', {
                    status: order.delivery_status,
                  })}
                </span>
              </Discrepancy>
            )}
            <OvOrderDeliveryDates
              order={order}
              overdueDays={
                deliveryState.kind === 'overdue'
                  ? deliveryState.daysSinceShipped
                  : undefined
              }
            />
            <CarrierLine>
              <span>{t('orders.delivery.carrierStatus')}</span>
              {order.delivery_status
                ? getDeliveryStatusLabel(order.delivery_status, t)
                : t('orders.delivery.notProvided')}
            </CarrierLine>
          </>
        );

      default: {
        const unhandledState: never = deliveryState;

        return unhandledState;
      }
    }
  };

  return (
    <OvRecordDetailsSection
      title={t('orders.details.fulfillmentAndDelivery')}
      action={
        order.tracking_url && (
          <a
            href={order.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('orders.delivery.trackWithCarrier')}
          </a>
        )
      }
    >
      <Context>
        {context.map(({ label, value }) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </Context>
      {renderDelivery()}
    </OvRecordDetailsSection>
  );
};

export default OvOrderDeliverySection;

const Context = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.125rem;
  margin: 0;

  div {
    display: flex;
    align-items: baseline;
    gap: 0.3125rem;
  }

  dt {
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
  }

  dd {
    margin: 0;
  }
`;

const QuietEmpty = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  margin-top: 0.875rem;
  padding: 0.875rem 0.75rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.OV_WHITE};

  p {
    margin: 0;
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
  }
`;

const Discrepancy = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4375rem;
  margin: 0.875rem 0;
  padding: 0.625rem 0.75rem;
  border-radius: ${Variables.borderRadius.SMALL};
  background-color: ${Colours.OV_YELLOW};
  font-size: ${Variables.fontSizes.SMALL};

  .MuiSvgIcon-root {
    width: 1.0625rem;
    height: 1.0625rem;
  }
`;

const CarrierLine = styled.p`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin: 0;

  span {
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
  }
`;
